import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Video, Camera, PhoneCall, Trash2, Edit3, DollarSign, Calendar, User as UserIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADOS = [
  { id: 'pendiente', label: 'Pendiente', color: 'border-yellow-500/40', dot: 'bg-yellow-500', bg: 'bg-yellow-500/5' },
  { id: 'en_produccion', label: 'En Producción', color: 'border-blue-500/40', dot: 'bg-blue-500', bg: 'bg-blue-500/5' },
  { id: 'entregado', label: 'Entregado por Modelo', color: 'border-purple-500/40', dot: 'bg-purple-500', bg: 'bg-purple-500/5' },
  { id: 'enviado', label: 'Enviado al Cliente', color: 'border-cyan-500/40', dot: 'bg-cyan-500', bg: 'bg-cyan-500/5' },
  { id: 'pagado', label: 'Pagado', color: 'border-green-500/40', dot: 'bg-green-500', bg: 'bg-green-500/5' },
];

const TIPOS = {
  custom_video: { label: 'Custom Video', icon: Video, color: 'text-pink-400' },
  custom_photo: { label: 'Custom Foto', icon: Camera, color: 'text-rose-400' },
  videollamada: { label: 'Videollamada', icon: PhoneCall, color: 'text-purple-400' },
};

const VC_STEPS = [
  { n: 1, label: 'Pago recibido', desc: 'Chatter reporta + pago confirmado', estado: 'en_produccion' },
  { n: 2, label: 'Info a Modelo', desc: 'Asistente envía detalles a la modelo', estado: 'en_produccion' },
  { n: 3, label: 'Link Meet enviado', desc: 'Link generado y enviado al fan', estado: 'enviado' },
  { n: 4, label: 'Llamada realizada', desc: 'Modelo completó la videollamada', estado: 'pagado' },
];

export default function SolicitudesPanel({ currentUserRole }) {
  const [requests, setRequests] = useState([]);
  const [models, setModels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterModelo, setFilterModelo] = useState('all');

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const blankForm = {
    modelo_id: '',
    tipo: 'custom_video',
    cliente_username: '',
    monto: 0,
    fecha_entrega: '',
    instrucciones: '',
  };
  const [form, setForm] = useState(blankForm);

  const isPrivileged = ['administrativo', 'ceo', 'asistente'].includes(currentUserRole);

  const fetchAll = async () => {
    setLoading(true);
    const params = filterModelo !== 'all' ? `?modelo_id=${filterModelo}` : '';
    
    // Fetch modelos - intenta primero el endpoint completo, si falla usa el simple
    try {
      const mRes = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(Array.isArray(mRes.data) ? mRes.data : []);
    } catch (err) {
      console.error('[Solicitudes] /api/models falló:', err?.response?.status, err?.response?.data);
      // Fallback al endpoint mínimo
      try {
        const mFallback = await axios.get(`${API}/models/list`, { withCredentials: true });
        setModels(Array.isArray(mFallback.data) ? mFallback.data : []);
      } catch (err2) {
        console.error('[Solicitudes] /api/models/list también falló:', err2?.response?.status);
        setModels([]);
      }
    }
    
    // Fetch solicitudes
    try {
      const rRes = await axios.get(`${API}/content-requests${params}`, { withCredentials: true });
      setRequests(Array.isArray(rRes.data) ? rRes.data : []);
    } catch (err) {
      console.error('[Solicitudes] /content-requests:', err?.response?.status);
      setRequests([]);
    }
    
    // Fetch stats
    try {
      const sRes = await axios.get(`${API}/content-requests/stats`, { withCredentials: true });
      setStats(sRes.data || null);
    } catch (err) {
      console.error('[Solicitudes] /content-requests/stats:', err?.response?.status);
      setStats(null);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [filterModelo]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setOpenDialog(true);
  };

  const openEdit = (req) => {
    setEditing(req);
    setForm({
      modelo_id: req.modelo_id,
      tipo: req.tipo,
      cliente_username: req.cliente_username,
      monto: req.monto,
      fecha_entrega: req.fecha_entrega || '',
      instrucciones: req.instrucciones || '',
    });
    setOpenDialog(true);
  };

  // Auto-fill monto when modelo or tipo changes
  const handleModeloChange = (modelo_id) => {
    const mdl = models.find((m) => m.id === modelo_id);
    if (!mdl) {
      setForm({ ...form, modelo_id });
      return;
    }
    const priceMap = {
      custom_video: mdl.precio_custom_video || 0,
      custom_photo: mdl.precio_custom_photo || 0,
      videollamada: mdl.precio_videollamada || 0,
    };
    setForm({ ...form, modelo_id, monto: editing ? form.monto : priceMap[form.tipo] || 0 });
  };

  const handleTipoChange = (tipo) => {
    const mdl = models.find((m) => m.id === form.modelo_id);
    const priceMap = mdl
      ? {
          custom_video: mdl.precio_custom_video || 0,
          custom_photo: mdl.precio_custom_photo || 0,
          videollamada: mdl.precio_videollamada || 0,
        }
      : {};
    setForm({ ...form, tipo, monto: editing ? form.monto : priceMap[tipo] || 0 });
  };

  const handleSave = async () => {
    if (!form.modelo_id || !form.tipo || !form.cliente_username) {
      toast.error('Modelo, tipo y cliente son obligatorios');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/content-requests/${editing.id}`, form, { withCredentials: true });
        toast.success('Solicitud actualizada');
      } else {
        await axios.post(`${API}/content-requests`, form, { withCredentials: true });
        toast.success('Solicitud creada');
      }
      setOpenDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await axios.delete(`${API}/content-requests/${id}`, { withCredentials: true });
      toast.success('Solicitud eliminada');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const changeEstado = async (req, nuevoEstado) => {
    try {
      await axios.put(`${API}/content-requests/${req.id}`, { estado: nuevoEstado }, { withCredentials: true });
      toast.success(`Movido a ${nuevoEstado.replace('_', ' ')}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const changeVcStep = async (req, nuevoStep) => {
    const stepDef = VC_STEPS.find((s) => s.n === nuevoStep);
    if (!stepDef) return;
    try {
      await axios.put(
        `${API}/content-requests/${req.id}`,
        { videollamada_step: nuevoStep, estado: stepDef.estado },
        { withCredentials: true }
      );
      toast.success(`Paso ${nuevoStep}: ${stepDef.label}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar paso');
    }
  };

  if (loading) return <div className="text-gray-400 text-center py-12">Cargando solicitudes...</div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4">
            <p className="text-gray-400 text-xs">Total Solicitudes</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </Card>
          <Card className="bg-[#0a0a0a] border border-yellow-500/20 rounded-2xl p-4">
            <p className="text-yellow-400 text-xs">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.by_estado.pendiente}</p>
          </Card>
          <Card className="bg-[#0a0a0a] border border-blue-500/20 rounded-2xl p-4">
            <p className="text-blue-400 text-xs">En Producción</p>
            <p className="text-2xl font-bold text-blue-400">{stats.by_estado.en_produccion}</p>
          </Card>
          <Card className="bg-[#0a0a0a] border border-orange-500/20 rounded-2xl p-4">
            <p className="text-orange-400 text-xs">💰 Pipeline</p>
            <p className="text-2xl font-bold text-orange-400">${stats.monto_pipeline.toLocaleString()}</p>
          </Card>
          <Card className="bg-[#0a0a0a] border border-green-500/20 rounded-2xl p-4">
            <p className="text-green-400 text-xs">✅ Pagado</p>
            <p className="text-2xl font-bold text-green-400">${stats.monto_pagado.toLocaleString()}</p>
          </Card>
        </div>
      )}

      {/* Header + Actions */}
      <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl">
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <CardTitle className="text-white">Solicitudes de Contenido</CardTitle>
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            <Select value={filterModelo} onValueChange={setFilterModelo}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-full md:w-56" data-testid="filter-modelo-solicitudes">
                <SelectValue placeholder="Filtrar modelo" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="all" className="text-white">Todas las modelos</SelectItem>
                {models.filter(m => m.status !== 'inactiva').map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button
                  onClick={openCreate}
                  className="bg-pink-600 hover:bg-pink-700 text-white"
                  data-testid="create-request-btn"
                >
                  + Nueva Solicitud
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editing ? 'Editar Solicitud' : 'Nueva Solicitud'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                  <div>
                    <Label className="text-gray-300">Modelo *</Label>
                    <Select value={form.modelo_id} onValueChange={handleModeloChange}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Selecciona modelo" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {models.filter(m => m.status !== 'inactiva').map((m) => (
                          <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Tipo *</Label>
                    <Select value={form.tipo} onValueChange={handleTipoChange}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {Object.entries(TIPOS).map(([k, v]) => (
                          <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Cliente / Usuario *</Label>
                    <Input
                      value={form.cliente_username}
                      onChange={(e) => setForm({ ...form, cliente_username: e.target.value })}
                      placeholder="@usuario_plataforma"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-300">Monto ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.monto}
                        onChange={(e) => setForm({ ...form, monto: parseFloat(e.target.value) || 0 })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Fecha entrega</Label>
                      <Input
                        type="date"
                        value={form.fecha_entrega}
                        onChange={(e) => setForm({ ...form, fecha_entrega: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">Instrucciones</Label>
                    <Textarea
                      value={form.instrucciones}
                      onChange={(e) => setForm({ ...form, instrucciones: e.target.value })}
                      placeholder="Detalles del pedido, escenario, ropa, duración..."
                      className="bg-zinc-800 border-zinc-700 text-white min-h-[90px]"
                    />
                  </div>
                  <p className="text-xs text-gray-500 italic">
                    💡 Si el monto es 0, se usará la tarifa default de la modelo automáticamente.
                  </p>
                  <Button onClick={handleSave} className="w-full bg-pink-600 hover:bg-pink-700">
                    {editing ? 'Guardar cambios' : 'Crear Solicitud'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Kanban */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {ESTADOS.map((estado) => {
              const items = requests.filter((r) => r.estado === estado.id);
              return (
                <div
                  key={estado.id}
                  className={`rounded-xl border ${estado.color} ${estado.bg} p-3 min-h-[200px]`}
                  data-testid={`kanban-col-${estado.id}`}
                >
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/5">
                    <span className={`w-2 h-2 rounded-full ${estado.dot}`}></span>
                    <h3 className="text-white text-sm font-semibold">{estado.label}</h3>
                    <span className="text-xs text-gray-400 ml-auto">{items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {items.length === 0 && (
                      <p className="text-xs text-gray-600 italic text-center py-4">Vacío</p>
                    )}
                    {items.map((req) => {
                      const Tipo = TIPOS[req.tipo] || TIPOS.custom_video;
                      const TipoIcon = Tipo.icon;
                      return (
                        <Card
                          key={req.id}
                          className="bg-zinc-900 border border-white/10 hover:border-pink-500/40 transition-all p-3"
                          data-testid={`request-card-${req.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 text-xs">
                              <TipoIcon className={`w-3.5 h-3.5 ${Tipo.color}`} />
                              <span className={`font-medium ${Tipo.color}`}>{Tipo.label}</span>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => openEdit(req)}
                                className="text-blue-400 hover:text-blue-300"
                                title="Editar"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(req.id)}
                                className="text-red-400 hover:text-red-300"
                                title="Eliminar"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="text-white text-sm font-semibold mb-1">{req.modelo_name}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-1 mb-2">
                            <UserIcon className="w-3 h-3" /> {req.cliente_username}
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <div className="text-green-400 font-bold flex items-center gap-0.5">
                              <DollarSign className="w-3 h-3" />
                              {(req.monto || 0).toLocaleString()}
                            </div>
                            {req.fecha_entrega && (
                              <div className="text-gray-400 flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {req.fecha_entrega}
                              </div>
                            )}
                          </div>
                          {req.instrucciones && (
                            <div className="text-xs text-gray-500 mt-2 line-clamp-2 italic">
                              {req.instrucciones}
                            </div>
                          )}
                          <div className="text-[10px] text-gray-600 mt-2 pt-2 border-t border-white/5">
                            por {req.created_by_name}
                          </div>

                          {/* Stepper específico para videollamadas */}
                          {req.tipo === 'videollamada' && (
                            <div className="mt-3 pt-2 border-t border-purple-500/20">
                              <div className="text-[10px] uppercase tracking-wide text-purple-300 font-semibold mb-2">
                                Flujo videollamada
                              </div>
                              <div className="space-y-1">
                                {VC_STEPS.map((s) => {
                                  const currentStep = req.videollamada_step || 0;
                                  const reached = currentStep >= s.n;
                                  const isCurrent = currentStep === s.n;
                                  const canClick = isPrivileged && (s.n === currentStep + 1 || isCurrent || s.n < currentStep);
                                  return (
                                    <button
                                      key={s.n}
                                      type="button"
                                      data-testid={`vc-step-${req.id}-${s.n}`}
                                      disabled={!canClick}
                                      onClick={() => canClick && changeVcStep(req, s.n)}
                                      className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded-md text-[11px] transition-all ${
                                        isCurrent
                                          ? 'bg-purple-500/20 border border-purple-400/50 text-purple-100'
                                          : reached
                                          ? 'text-purple-300 hover:bg-purple-500/10'
                                          : canClick
                                          ? 'text-gray-400 hover:bg-zinc-800'
                                          : 'text-gray-600 cursor-not-allowed'
                                      }`}
                                      title={s.desc}
                                    >
                                      <span
                                        className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold border ${
                                          reached
                                            ? 'bg-purple-500 border-purple-400 text-white'
                                            : 'border-zinc-600 text-gray-500'
                                        }`}
                                      >
                                        {reached ? '✓' : s.n}
                                      </span>
                                      <span className="flex-1 truncate">{s.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Move buttons - solo se muestra el dropdown si NO es videollamada o si no es privilegiado */}
                          {isPrivileged && req.tipo !== 'videollamada' && (
                            <div className="flex gap-1 mt-2">
                              <Select
                                value={req.estado}
                                onValueChange={(v) => changeEstado(req, v)}
                              >
                                <SelectTrigger
                                  className="bg-zinc-800 border-zinc-700 text-white text-xs h-7"
                                  data-testid={`change-estado-${req.id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                  {ESTADOS.map((e) => (
                                    <SelectItem key={e.id} value={e.id} className="text-white text-xs">
                                      {e.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
